import {
  GetStaticPaths,
  GetStaticPropsContext,
  InferGetStaticPropsType,
  NextPage,
} from 'next';

import { buildCollectionEventFilter } from 'utils/collection-filters.utils';
import { getCurrentISOString } from 'utils/date.utils';
import { getDefaultLocale } from 'utils/i18n.utils';
import { generateStaticPaths, replacePathParams } from 'utils/routes.utils';

import { Duration } from 'constants/duration.constants';
import { EVENTS_PER_PAGE_COLLECTION } from 'constants/events.constant';
import { NOT_FOUND } from 'constants/nextjs.constants';
import { Routes } from 'constants/routes.constants';
import {
  collectionEventsQuery,
  pageCollectionQuery,
} from 'queries/collections/queries';
import { datoClient } from 'services/api';
import Prefetcher from 'services/prefetcher';
import { AllPageCollectionSlugsDocument } from 'types/api/dato-operations.types';

import CollectionDetailContainer from 'components/@collections/CollectionDetailContainer';
import AppNavigation from 'components/@navigation/AppNavigation';
import PageSeo from 'components/@seo/PageSeo';

const CollectionDetail: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ collection }) => {
  return (
    <AppNavigation hideBottomNavigation>
      <PageSeo tags={collection.seoTags} />
      <CollectionDetailContainer key={collection.id} collection={collection} />
    </AppNavigation>
  );
};

export default CollectionDetail;

export const getStaticPaths: GetStaticPaths = async ({
  locales,
  defaultLocale,
}) => {
  const locale = getDefaultLocale({ defaultLocale });

  const { allPageCollections } = await datoClient().request(
    AllPageCollectionSlugsDocument,
    { locale },
  );

  const paths = generateStaticPaths(allPageCollections, locales, ({ slug }) =>
    replacePathParams(Routes.CollectionDetail, { slug }),
  );

  return { paths, fallback: 'blocking' };
};

export const getStaticProps = async ({
  locale,
  params,
  preview,
}: GetStaticPropsContext<{ slug: string }>) => {
  const prefetcher = await Prefetcher.init({ locale, preview });

  const slug = params?.slug;

  if (!slug) return { ...NOT_FOUND };

  const { pageCollection } = await prefetcher.prefetch(pageCollectionQuery, {
    slug,
  });

  if (!pageCollection) return { ...NOT_FOUND };

  // Build GraphQL filter based on collection settings
  const eventFilter = buildCollectionEventFilter(
    {
      collections: { allIn: [pageCollection.id] },
      endDatetime: { gte: getCurrentISOString() },
      unlistedEvent: { eq: false },
    },
    {
      timeFilter: pageCollection.timeFilter,
      venue: pageCollection.venue,
    },
  );

  await prefetcher.prefetchInfinite(
    collectionEventsQuery,
    {
      filter: eventFilter,
      first: EVENTS_PER_PAGE_COLLECTION,
      orderBy: 'startDatetime_ASC',
      skip: 0,
    },
    { meta: { dehydrate: true } },
  );

  return {
    props: {
      collection: pageCollection,
      dehydratedState: prefetcher.dehydratedState,
    },
    revalidate: Duration.TEN_MIN,
  };
};
